import { Component, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { CustomerListItem } from '@models/customers';
import { PartnerToggleOptions } from '@models/partners';
import { ColDef } from 'ag-grid-community';
import { CuiGuardLoadingIndicatorComponent, CuiTableModule, formatDate } from 'cui-components';
import { CustomersStore, CustomersStoreType } from 'src/app/app-state/customers/customers.store';

export const CUSTOMERS_TABLE_ID = 'customers-table';

@Component({
  imports: [CuiTableModule, RouterModule, CuiGuardLoadingIndicatorComponent],
  selector: 'hui-customers',
  template: `
    <cui-table
      [tableId]="CUSTOMERS_TABLE_ID"
      id="customers-table"
      [columnDefs]="columnDefs"
      [rowData]="customers()"
      [toggleDefaultOption]="'Operative'"
      [toggleOptions]="ToggleOptions"
      [isLoading]="isLoading()"
      [selectedRowId]="selectedCustomerId()"
      [navigationFn]="navigateToCustomer"
      (toggleClicked)="onToggleClick($event)"
    ></cui-table>

    <router-outlet></router-outlet>
    <cui-guard-loading-indicator [urlToCheck]="'/customers'"></cui-guard-loading-indicator>
  `,
  styleUrls: ['./customers.component.scss'],
})
export class CustomersComponent {
  private customersStore: CustomersStoreType = inject(CustomersStore);

  customers = this.customersStore.customers;
  isLoading = this.customersStore.isLoading;
  selectedCustomerId = this.customersStore.selectedCustomerId;
  toggleOption = this.customersStore.toggle;

  CUSTOMERS_TABLE_ID = CUSTOMERS_TABLE_ID;
  ToggleOptions: PartnerToggleOptions[] = [PartnerToggleOptions.Operative, PartnerToggleOptions.All];
  columnDefs: ColDef<CustomerListItem>[] = [
    {
      headerName: 'Name',
      valueGetter: params => (params.data?.ownPartner ? `${params.data?.name} (own partner)` : params.data?.name),
    },
    {
      headerName: 'Country',
      field: 'address.country',
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      filterParams: {
        applyMiniFilterWhileTyping: true,
      },
    },
    {
      headerName: 'Address',
      field: 'address.street',
    },
    {
      headerName: 'City',
      field: 'address.city',
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      filterParams: {
        applyMiniFilterWhileTyping: true,
      },
    },
    {
      headerName: 'Post code',
      field: 'address.postCode',
    },
    {
      headerName: 'State',
      field: 'address.state',
    },
    {
      headerName: 'Status',
      field: 'status',
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      filterParams: {
        applyMiniFilterWhileTyping: true,
      },
      cellRenderer: 'cuiTableCellColoredChipRenderer',
    },
    {
      headerName: 'Marked for deletion date',
      field: 'markedForDeletionDateTimeUtc',
      valueFormatter: ({ value }) => (value ? formatDate(new Date(value), 'yyyy-MM-dd') : ''),
    },
    {
      headerName: 'Activated for Cloud Hub',
      field: 'applicationActivated',
      cellRenderer: 'cuiTableCellCheckmarkRenderer',
      valueFormatter: ({ value }) => (value ? 'Yes' : 'No'),
    },
    {
      field: 'id',
      hide: true,
    },
    {
      field: 'entraTenantId',
      hide: true,
    },
  ];

  constructor() {
    this.customersStore.loadCustomers();
  }

  navigateToCustomer = (item: CustomerListItem): Promise<boolean> => this.customersStore.goToCustomerActions(item.id);

  onToggleClick(toggle: PartnerToggleOptions): void {
    this.customersStore.loadCustomers(toggle);
  }
}
